import React, { useState, useEffect } from "react";
import { Box, Text, VStack, Flex, HStack, Button, Icon, Spinner } from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { Header } from "build-ts/metadata";
import { MetadataTypeEnum, metadataTypeEnumFromJSON } from "build-ts/enums";
import CustomModal from "../../components/modals/CustomModal";
import { deleteAnalyst } from "services/AnalystService";
import { deleteBenchmark } from "services/MetricService";
import { AppIcons } from "constants/icons";
import showToast from "hooks/useCustomToast";
import { useNavigate } from "react-router-dom";

interface DependenciesModalProps {
  isOpen: boolean;
  onClose: () => void;
  dependencies: Header[];
  onViewMKG?: (metric: Header) => void;
}

const DependenciesModal: React.FC<DependenciesModalProps> = ({ isOpen, onClose, dependencies, onViewMKG }) => {
  const navigate = useNavigate();
  const [localDependencies, setLocalDependencies] = useState<Header[]>(dependencies);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setLocalDependencies(dependencies);
  }, [dependencies]);

  const metrics = localDependencies.filter(dep => metadataTypeEnumFromJSON(dep.type) === MetadataTypeEnum.METRIC);
  const benchmarks = localDependencies.filter(dep => metadataTypeEnumFromJSON(dep.type) === MetadataTypeEnum.BENCHMARK);
  const analysts = localDependencies.filter(dep => metadataTypeEnumFromJSON(dep.type) === MetadataTypeEnum.ANALYST);

  const handleDeleteAnalyst = async (analyst: Header) => {
    if (!analyst.id) return;
    setDeletingIds(prev => new Set(prev).add(analyst.id!));
    const result = await deleteAnalyst(analyst.id);
    setDeletingIds(prev => {
      const next = new Set(prev);
      next.delete(analyst.id!);
      return next;
    });
    // deleteAnalyst returns a Response on success, null on error (service shows error toast)
    if (result) {
      showToast("Analyst deleted successfully", "", "success");
      setLocalDependencies(prev => prev.filter(dep => dep.id !== analyst.id));
    }
  };

  const handleDeleteBenchmark = async (benchmark: Header) => {
    if (!benchmark.id) return;
    setDeletingIds(prev => new Set(prev).add(benchmark.id!));
    const result = await deleteBenchmark(benchmark.id);
    setDeletingIds(prev => {
      const next = new Set(prev);
      next.delete(benchmark.id!);
      return next;
    });
    // deleteBenchmark: null = success, non-null object = 409 with deps (service shows error toast otherwise)
    if (!result) {
      showToast("Benchmark deleted successfully", "", "success");
      setLocalDependencies(prev => prev.filter(dep => dep.id !== benchmark.id));
    }
  };

  const isDeleting = (id: string | undefined) => Boolean(id && deletingIds.has(id));

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title="Dependency Confirmation"
      maxWidth="560px"
      maxHeight="90vh"
      isCentered={true}
      motionPreset="scale"
      overlayBg="blackAlpha.300"
    >
      <Box px={4} pb={4} pt={1}>
        <Text fontSize="sm" color="gray.500" mb={4} mt={0}>
          The following dependencies must be resolved before this item can be deleted.
        </Text>

        <VStack align="stretch" spacing={5}>
          {/* ── Analysts ── */}
          {analysts.length > 0 && (
            <Box>
              <HStack spacing={2} mb={2}>
                <Icon as={AppIcons.analyst} boxSize={3.5} color="gray.400" />
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  mb={0}
                >
                  Analysts · {analysts.length}
                </Text>
              </HStack>
              <VStack align="stretch" spacing={1.5}>
                {analysts.map(analyst => (
                  <Box
                    key={analyst.id}
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    px={3}
                    py={2.5}
                    bg="white"
                    _hover={{ borderColor: "gray.300", bg: "gray.50" }}
                    transition="border-color 0.15s, background 0.15s"
                  >
                    <Flex justify="space-between" align="center" gap={3}>
                      <Text fontSize="sm" fontWeight="600" color="gray.800" mb={0} noOfLines={1} flex={1} minW={0}>
                        {analyst.name}
                      </Text>
                      <HStack spacing={1.5} flexShrink={0}>
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="gray"
                          rightIcon={<ExternalLinkIcon boxSize={3} />}
                          onClick={() => window.open(`/analyst/${analyst.id}`, "_blank")}
                        >
                          View
                        </Button>
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="red"
                          isDisabled={isDeleting(analyst.id)}
                          onClick={() => handleDeleteAnalyst(analyst)}
                          leftIcon={
                            isDeleting(analyst.id) ? (
                              <Spinner size="xs" />
                            ) : (
                              <Icon as={AppIcons.deleteLine} boxSize={3} />
                            )
                          }
                        >
                          Delete
                        </Button>
                      </HStack>
                    </Flex>
                  </Box>
                ))}
              </VStack>
            </Box>
          )}

          {/* ── Metrics ── */}
          {metrics.length > 0 && (
            <Box>
              <HStack spacing={2} mb={2}>
                <Icon as={AppIcons.metric} boxSize={3.5} color="gray.400" />
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  mb={0}
                >
                  Metrics · {metrics.length}
                </Text>
              </HStack>
              <VStack align="stretch" spacing={1.5}>
                {metrics.map(metric => (
                  <Box
                    key={metric.id}
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    px={3}
                    py={2.5}
                    bg="white"
                    _hover={{ borderColor: "gray.300", bg: "gray.50" }}
                    transition="border-color 0.15s, background 0.15s"
                  >
                    <Flex justify="space-between" align="center" gap={3}>
                      <Text fontSize="sm" fontWeight="600" color="gray.800" mb={0} noOfLines={1} flex={1} minW={0}>
                        {metric.name}
                      </Text>
                      {onViewMKG && (
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="purple"
                          flexShrink={0}
                          onClick={() => onViewMKG(metric)}
                        >
                          View MKG
                        </Button>
                      )}
                    </Flex>
                  </Box>
                ))}
              </VStack>
            </Box>
          )}

          {/* ── Benchmarks ── */}
          {benchmarks.length > 0 && (
            <Box>
              <HStack spacing={2} mb={2}>
                <Icon as={AppIcons.benchmark} boxSize={3.5} color="gray.400" />
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  mb={0}
                >
                  Benchmarks · {benchmarks.length}
                </Text>
              </HStack>
              <VStack align="stretch" spacing={1.5}>
                {benchmarks.map(benchmark => (
                  <Box
                    key={benchmark.id}
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    px={3}
                    py={2.5}
                    bg="white"
                    _hover={{ borderColor: "gray.300", bg: "gray.50" }}
                    transition="border-color 0.15s, background 0.15s"
                  >
                    <Flex justify="space-between" align="center" gap={3}>
                      <Text fontSize="sm" fontWeight="600" color="gray.800" mb={0} noOfLines={1} flex={1} minW={0}>
                        {benchmark.name}
                      </Text>
                      <HStack spacing={1.5} flexShrink={0}>
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="gray"
                          rightIcon={<ExternalLinkIcon boxSize={3} />}
                          onClick={() => navigate(`/mkg/benchmarks/${benchmark.id}`)}
                        >
                          View
                        </Button>
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="red"
                          isDisabled={isDeleting(benchmark.id)}
                          onClick={() => handleDeleteBenchmark(benchmark)}
                          leftIcon={
                            isDeleting(benchmark.id) ? (
                              <Spinner size="xs" />
                            ) : (
                              <Icon as={AppIcons.deleteLine} boxSize={3} />
                            )
                          }
                        >
                          Delete
                        </Button>
                      </HStack>
                    </Flex>
                  </Box>
                ))}
              </VStack>
            </Box>
          )}

          {localDependencies.length === 0 && (
            <Text fontSize="sm" color="gray.400" textAlign="center" py={4} mb={0}>
              All dependencies resolved. You may now retry the deletion.
            </Text>
          )}
        </VStack>
      </Box>
    </CustomModal>
  );
};

export default DependenciesModal;
